import type { Lang } from '../i18n'
import { de } from './i18n/de'
import { es } from './i18n/es'
import { th } from './i18n/th'
import { zh } from './i18n/zh'

/**
 * Case studies for the «Work» section. Client names and real data are withheld under NDA:
 * interfaces are redrawn from scratch and every value shown inside them is illustrative.
 * Figures in the copy are rounded but kept close to the measured facts.
 */

export type ModuleId =
  | 'erp' | 'portal' | 'catalog' | 'search' | 'agent'
  | 'grid-model' | 'grid-asset' | 'grid-monitor' | 'grid-works'
  | 'panel-spec' | 'panel-scheme' | 'panel-docs' | 'panel-bom'

export type CaseModule = {
  id: ModuleId
  tab: string
  title: string
  lead: string
  points: string[]
}

export type CaseStudyCopy = {
  id: 'platform' | 'grid' | 'panel'
  sector: string
  title: string
  lead: string
  standard?: string
  before: string
  after: string
  metrics: Array<{ value: string; label: string }>
  modules: CaseModule[]
  results?: Array<{ title: string; text: string }>
}

type Pair = [string, string]

export type CasesCopy = {
  note: string
  labels: { before: string; after: string; results: string; modules: string }
  flagship: CaseStudyCopy
  energy: CaseStudyCopy[]
  /** Short strings drawn inside the interface mock-ups. */
  ui: {
    erp: { columns: [string, string, string, string]; modules: [string, string, string, string]; search: string; qty: string }
    portal: { search: string; qty: string; find: string; supplier: string; stock: string; lead: string; price: string; total: string; best: string; weeks: string; suppliers: [string, string, string, string] }
    catalog: { query: string; section: string; positions: string; filters: string; apply: string; relevance: string; partNumber: string; maker: string; median: string; inStock: string; demand: string; analogs: string; datasheet: string; offers: string; trend: string }
    search: { title: string; rows: [string, string, string, string, string]; exact: string; byParams: string; analog: string; unknown: string; counter: string; file: string; fileResult: string; find: string; toCart: string }
    agent: { title: string; user1: string; tool1: string; agent1: string; user2: string; tool2: string; agent2: string; confirm: string; user3: string; tool3: string; agent3: string; invoice: string; typing: string }
    grid: { editor: string; scheme: string; layers: string[]; legend: [string, string, string]; substation: string; search: string }
    tower: { title: string; photo: string; rows: Pair[]; history: string; events: Pair[]; add: string }
    monitor: { title: string; cols: [string, string, string, string]; rows: Array<[string, string, number, string]>; schedule: string; weeks: string[]; resources: string; crew: string }
    works: { title: string; columns: [string, string, string]; items: Array<[string, string, number]>; crew: string; crewStatus: string; connection: string }
    panel: { units: string; settings: string; params: Pair[]; front: string; export: string; feeder: string }
    spec: { title: string; fields: Pair[]; warning: string; fix: string; recalc: string; build: string }
    docs: { departments: [string, string, string]; items: [string[], string[], string[]]; approved: string; editing: string }
    bom: { title: string; cols: [string, string, string, string]; rows: Array<[string, string, string, string]>; total: string; totalValue: string; maker: string }
  }
}

const ru: CasesCopy = {
  note: 'Названия клиентов и реальные данные скрыты по NDA. Интерфейсы перерисованы специально для сайта, значения в них условные.',
  labels: { before: 'Было', after: 'Стало', results: 'Что получил клиент', modules: 'Модули' },
  flagship: {
    id: 'platform',
    sector: 'Дистрибуция электронных компонентов · международная торговля',
    title: 'Цифровая платформа дистрибьютора: от запроса клиента до таможни — и продажи через ИИ-агентов',
    lead: 'Единая система для международного дистрибьютора электронных компонентов: ERP и CRM для всех отделов, B2B-портал для клиентов, живой каталог, AI-поиск и заказ прямо из ИИ-ассистента покупателя. Каждый модуль мы спроектировали, построили и развиваем сами.',
    before: 'Закупки по телефону и в таблицах, документы по почте, сроки поставки считали вручную, ошибки — на каждом этапе.',
    after: 'Сквозная автоматизация от поиска поставщика до приёмки на склад: каждый заказ виден на каждом шаге, а покупатель находит и заказывает деталь сам — на сайте или через своего ИИ-агента.',
    metrics: [
      { value: '~8 млн', label: 'позиций в каталоге' },
      { value: '15+', label: 'мировых дистрибьюторов в одной выдаче' },
      { value: '×17', label: 'быстрее открываются разделы каталога' },
      { value: '~1 мин', label: 'от запроса в ИИ-ассистенте до счёта' },
    ],
    modules: [
      {
        id: 'erp',
        tab: 'ERP и CRM',
        title: 'Операционное ядро компании',
        lead: 'Вся цепочка сделки в одной системе — от запроса клиента до закрывающих документов. Каждый день в ней работают 100+ сотрудников: руководство, менеджеры, закупки, логистика, инженеры, склад и курьеры.',
        points: [
          'Закупки: цены, лицензии, сертификация и сроки поставки',
          'Платежи: контроль оплат с учётом налоговых правил страны поставщика, рассрочки и предоплаты, договоры и счета в одном месте',
          'Логистика: отгрузка, консолидация на транзитных складах, трекинг перевозчиков и приёмка на склад',
          'Таможня: автоматизированная работа декларантов, подача документов, лицензии и сертификаты',
          'Интеграции с бухгалтерией, WMS, CRM и перевозчиками — без двойного ввода данных',
        ],
      },
      {
        id: 'portal',
        tab: 'B2B-портал',
        title: 'Самообслуживание для клиентов',
        lead: 'Клиент сам находит деталь, сравнивает предложения и оформляет заказ — без переписки с менеджером.',
        points: [
          'Предложения 15+ мировых дистрибьюторов в одной таблице',
          'Поиск по партномеру и сравнение цен в реальном времени — с ценовыми ступенями, минимальной партией и кратностью',
          'Лучшее предложение подсвечивается автоматически, заказ — в пару кликов',
          'Отслеживание доставки на каждом этапе',
        ],
      },
      {
        id: 'catalog',
        tab: 'Каталог',
        title: 'Живой каталог на ~8 млн позиций',
        lead: 'Основа всех сервисов: нормализованные данные, которые понимают и люди, и поисковые системы, и ИИ-агенты.',
        points: [
          '~8 млн позиций в ~940 разделах от ~4 700 производителей, ~80 млн разобранных технических параметров',
          'Умный поиск: «конд керам» — и открывается нужный раздел с уже выставленными фильтрами',
          'Карточка товара: понятное описание, даташит, цены и наличие по рынку, динамика за 3 месяца, спрос и аналоги по параметрам',
          'Каталог пополняется каждый день — из запросов клиентов, системы инженеров и данных поставщиков',
          'Готов к индексации: понятные адреса, описания на двух языках почти у всех карточек, раздел с фильтрами за ~1 с вместо ~17',
        ],
      },
      {
        id: 'search',
        tab: 'AI-поиск',
        title: 'Спецификация любого вида — в готовый заказ',
        lead: 'Клиенту не нужно готовить данные: система сама понимает, что перед ней, и подбирает лучшие предложения.',
        points: [
          'Партномера, описания параметрами и свободный текст вперемешку — до 100 строк за один поиск',
          'Файлы любого формата — таблицы, PDF, документы, фото и сканы; шапки и мусор отсекаются сами: из 70 строк документа остаются 13 позиций',
          'Описание «резистор 10 кОм 0402 1%» превращается в подходящие партномера с предложениями поставщиков',
          'Аналоги по определяющим параметрам, а не по похожим буквам в названии',
          'Лучшая цена с учётом минимальной партии, кратности и остатков — и сразу в корзину',
        ],
      },
      {
        id: 'agent',
        tab: 'ИИ-агент',
        title: 'Новый канал продаж: покупка из ИИ-ассистента клиента',
        lead: 'Мы подключили сайт к ИИ-агентам по стандарту MCP. Любой ИИ-агент, поддерживающий MCP, работает с магазином напрямую: клиент ставит задачу и подтверждает — остальное агент делает сам.',
        points: [
          'На вход — список партномеров, описание параметрами или спецификация до 50 строк',
          'Поиск → подбор → заказ → счёт примерно за минуту',
          'Оптимизатор собирает самый выгодный состав заказа с учётом минимальной партии, кратности, остатков и сроков',
          'Заказ — только от имени компании клиента и только после его подтверждения; счёт приходит прямо в диалог',
        ],
      },
    ],
  },
  energy: [
    {
      id: 'grid',
      sector: 'Энергетика · электрические сети',
      title: 'Цифровая модель электросети по стандарту CIM',
      lead: 'Программный комплекс для сетевой компании в рамках национальной программы цифровизации инфраструктуры. Он создаёт подробную цифровую модель всего предприятия — сети, оборудования, измерений и работ — и держит её актуальной: изменения появляются в системе сразу после выполнения работ.',
      standard: 'CIM · IEC 61970-301 · IEC 61968-11',
      before: 'Инженеры обходили линии пешком, данные жили в блокнотах и разрозненных таблицах, схемы — на бумаге. Перед каждой защитой тарифа сведения собирали вручную.',
      after: 'Одна цифровая модель сети, оборудования и работ, которую нельзя исказить, удалить или скрыть. Данные для тарифа, ремонтов и техприсоединения выгружаются в пару кликов.',
      metrics: [
        { value: 'в разы', label: 'ниже трудоёмкость планирования работ' },
        { value: '1 модель', label: 'вместо бумажных схем и разрозненных таблиц' },
        { value: 'CIM', label: 'международный стандарт модели сети и обмена данными' },
        { value: '∞', label: 'рабочих мест — стоимость лицензии от них не зависит' },
      ],
      modules: [
        {
          id: 'grid-model',
          tab: 'Модель сети',
          title: 'Единая схема электрических соединений',
          lead: 'Топологическая модель сети — основа диспетчерского центра. Она интегрируется с действующими диспетчерскими системами.',
          points: [
            'Интерактивная карта сети с привязкой к местности — на GIS и онлайн-картах',
            'Сегментация по классам напряжения, кабельным и воздушным линиям',
            'Редактор ЛЭП с импортом геоданных KML и GPX, редактор подстанций с однолинейными схемами',
            'Модель активов и база нормативно-справочной информации, связанные со схемой, — основа расчётной модели',
            'Обмен данными по стандартному протоколу CIM снижает стоимость интеграции с другими системами',
          ],
        },
        {
          id: 'grid-asset',
          tab: 'Паспорта объектов',
          title: 'У каждого объекта сети — полный паспорт',
          lead: 'По каждой опоре, линии, подстанции и единице оборудования в системе есть технические характеристики, фото, замеры и история действий.',
          points: [
            'Оборудование наглядно на карте: бригада легко находит объект на местности по диспетчерскому наименованию',
            'Паспортизация опор с фотофиксацией: материал, назначение, конструкция, принадлежность к линиям',
            'История работ, замеры и паспорта хранятся в системе и не теряются',
            'Сведения поступают от всех задействованных служб и сразу доступны ответственному персоналу',
          ],
        },
        {
          id: 'grid-monitor',
          tab: 'Мониторинг',
          title: 'Состояние оборудования и приоритеты ремонтов',
          lead: 'Модель измерений связана с моделью сети и моделью активов: система видит техническое состояние и сама подсказывает, что ремонтировать в первую очередь.',
          points: [
            'Мониторинг технического состояния оборудования в привязке к модели сети',
            'Анализ и оценка состояния — основа программ реконструкции и техперевооружения',
            'Алгоритмы ранжируют дефекты по критичности и важности оборудования',
            'Оптимальный график ремонтов с расчётом необходимых ресурсов',
            'Инвестпрограмма формируется заметно быстрее: оборудование уже упорядочено по приоритету',
          ],
        },
        {
          id: 'grid-works',
          tab: 'Ремонты и техприсоединение',
          title: 'Управление работами в поле',
          lead: 'Система контролирует ход ремонтных работ и технологического присоединения, а местоположение бригады всегда видно диспетчеру.',
          points: [
            'Планирование ремонтной программы: планово-предупредительный ремонт, по техническому состоянию или комбинированный',
            'Нормативы времени и численности персонала для каждого вида работ уже заложены в систему',
            'Оценка ресурсов на основе действующих справочников',
            'Сбор данных для технологического присоединения — быстрее, сроки подключения потребителей сокращаются',
          ],
        },
      ],
      results: [
        { title: 'Актуальные данные', text: 'Изменения в сети отражаются сразу после работ — информация о предприятии всегда актуальна' },
        { title: 'Защищённая информация', text: 'При комплексном внедрении данные о сети невозможно исказить, удалить или скрыть; доступ разграничен по ролям' },
        { title: 'Фиксированная стоимость', text: 'Лицензия не привязана к числу рабочих мест' },
        { title: 'Любое устройство', text: 'Работа из браузера — в офисе, в диспетчерской и в поле' },
        { title: 'Меньше зависимость от людей', text: 'Знания о сети хранятся в системе, а не у «ключевых» сотрудников; интерфейс осваивается за короткое обучение' },
        { title: 'Гибкость', text: 'Система адаптируется под разное оборудование и расширяется новыми модулями' },
      ],
    },
    {
      id: 'panel',
      sector: 'Энергетика · инженерный конфигуратор',
      title: 'Трансформаторная подстанция с полной документацией — за 10 минут',
      lead: 'Веб-система для крупного предприятия энергетического сектора: полный расчёт комплектных трансформаторных подстанций (КТП, БКТП, КТПС, КТПМ) и низковольтных щитов с технической и коммерческой документацией. Инженер или менеджер описывает требования — система подбирает оптимальное решение и готовит документы для каждого отдела.',
      before: 'Компоновку, внешний вид, подбор аппаратуры, спецификации и бюджет готовили вручную — часы, а то и дни на каждый проект. Любое изменение техзадания означало пересчёт с нуля.',
      after: 'Требования на входе — оптимальное решение и полный пакет документов на выходе, не более чем за 10 минут. При изменении ТЗ система пересчитывает только то, что затронуто.',
      metrics: [
        { value: '≤10 мин', label: 'полный расчёт подстанции с документацией' },
        { value: '10+', label: 'документов на проект — автоматически' },
        { value: '3 отдела', label: 'получают свой пакет: продажи, снабжение, производство' },
        { value: '0', label: 'несовместимых вариантов — система их не пропустит' },
      ],
      modules: [
        {
          id: 'panel-spec',
          tab: 'Техзадание',
          title: 'Техническое задание онлайн',
          lead: 'Все ключевые требования описываются в одной форме и в любой момент корректируются — решение обновляется сразу.',
          points: [
            'Логика подбора оборудования учитывает технические требования, каталоги производителей, руководства и стандарты',
            'Подсказки упрощают подбор, а несовместимые варианты выбрать невозможно',
            'Данные каталогов производителей уже занесены в систему',
            'При изменении ТЗ достаточно поправить затронутые настройки — остальное система пересчитает сама',
          ],
        },
        {
          id: 'panel-scheme',
          tab: 'Схема и компоновка',
          title: 'Однолинейная схема и внешний вид — автоматически',
          lead: 'Интерактивный редактор с библиотекой функциональных блоков и автоматическим расчётом параметров установки.',
          points: [
            'Однолинейные схемы с библиотекой функциональных блоков',
            'Автоматический расчёт параметров установки',
            'Внешний вид и габариты в масштабе — клиент видит изделие до производства',
            'Единая методика проектирования, кто бы из инженеров ни вёл проект',
          ],
        },
        {
          id: 'panel-docs',
          tab: 'Документы',
          title: 'Свой пакет документов для каждого отдела',
          lead: 'Документы формируются автоматически, редактируются онлайн и проходят согласование и регистрацию в смежных отделах.',
          points: [
            'Коммерческий отдел: однолинейная схема, внешний вид, договор поставки и спецификация к нему, счёт на оплату, коммерческое предложение',
            'Снабжение: спецификации на аппаратуру, материалы, логистику, трудозатраты и накладные расходы',
            'Сборочный цех: внешний вид, однолинейная схема, спецификации и сборочные чертежи',
            'Экспорт в PDF, XLS и DXF',
          ],
        },
        {
          id: 'panel-bom',
          tab: 'Смета и настройка',
          title: 'Прозрачная смета и настройка под производителя',
          lead: 'Каждый шкаф, модуль и компонент — с ценой: руководитель видит, из чего складывается стоимость проекта.',
          points: [
            'Спецификация по шкафам, модулям и компонентам с ценами — автоматически',
            'Прозрачный обсчёт проекта для руководителя',
            'Универсальная настройка под конкретного производителя и предприятие',
            'Интеграция с другими программными комплексами и информационными системами',
          ],
        },
      ],
      results: [
        { title: 'КП за минуты', text: 'Клиент получает полную технико-коммерческую информацию в короткие сроки' },
        { title: 'Минимум ошибок', text: 'Несовместимое оборудование выбрать невозможно' },
        { title: 'Меньше рутины', text: 'Сотрудники освобождены от ручной компоновки, подбора аппаратуры и расчётов' },
        { title: 'Прозрачность', text: 'Руководитель видит весь обсчёт проекта' },
        { title: 'Изделие до производства', text: 'Внешний вид и габариты в масштабе видны заранее' },
        { title: 'Единый стандарт', text: 'Каждый проект — по одной методике, кто бы его ни вёл' },
      ],
    },
  ],
  ui: {
    erp: { columns: ['Запросы', 'В расчёте', 'Предложения готовы', 'Отправлено клиенту'], modules: ['Закупки', 'Платежи', 'Логистика', 'Таможня'], search: 'Поиск по партномеру или бренду', qty: 'шт' },
    portal: { search: 'Партномер', qty: 'Кол-во', find: 'Найти', supplier: 'Поставщик', stock: 'Наличие', lead: 'Срок', price: 'Цена, $', total: 'Сумма, $', best: 'Лучшее', weeks: 'нед.', suppliers: ['Дистрибьютор · Азия', 'Склад · Европа', 'Проверенный продавец', 'Официальный дистрибьютор'] },
    catalog: { query: 'конд керам', section: 'Керамические конденсаторы', positions: '~430 тыс. позиций', filters: 'Фильтры', apply: 'Применить', relevance: 'Релевантность', partNumber: 'Партномер', maker: 'Производитель', median: 'медианная цена', inStock: 'в наличии у 6 поставщиков', demand: 'Спрос', analogs: 'Аналоги', datasheet: 'Даташит', offers: 'Живые предложения', trend: '3 месяца' },
    search: { title: 'AI-поиск', rows: ['MAX3485ESA 700', 'резистор 10 кОм 0402 1% 25', 'BAV99 100', 'конденсатор 100 нФ 50 В X7R 0603 3000', 'какая-то непонятная строка'], exact: 'Точный партномер', byParams: 'По параметрам · 5 вариантов', analog: 'аналог', unknown: 'Уточнить строку', counter: '2 партномера · 3 описания', file: 'spec.pdf', fileResult: '70 строк → 13 позиций, 57 убрано', find: 'Найти', toCart: 'В корзину' },
    agent: {
      title: 'ИИ-ассистент клиента · MCP',
      user1: 'Найди BAV99, нужно 1000 штук',
      tool1: 'search_partnumber',
      agent1: 'Нашёл ~470 предложений от 16 поставщиков. На складах ~23 млн шт, самая быстрая поставка — 4 дня.',
      user2: 'Подбери самый дешёвый вариант с доставкой до 14 дней',
      tool2: 'get_best_offer',
      agent2: '1 000 шт по $0,004 — итого $4,00, срок 7 дней, самовывоз бесплатно.',
      confirm: 'Оформить заказ?',
      user3: 'Да, оформи и пришли счёт',
      tool3: 'create_order · get_invoice',
      agent3: 'Заказ оформлен от имени вашей компании. Счёт во вложении.',
      invoice: 'Счёт.pdf',
      typing: 'агент работает с сайтом…',
    },
    grid: { editor: 'Редактор', scheme: 'Схема сети', layers: ['Редактор ЛЭП', 'Подстанции', 'Опоры', 'Абоненты', 'Мониторинг'], legend: ['110 кВ', '10 кВ', '0,4 кВ'], substation: 'ПС-12 · 10/0,4 кВ', search: 'Адрес или объект' },
    panel: { units: 'Функциональные блоки', settings: 'Параметры', params: [['Напряжение', '380 В'], ['Частота', '50 Гц'], ['Ток шин', '630 А'], ['Степень защиты', 'IP42'], ['Резерв', '10 %']], front: 'Вид спереди', export: 'Экспорт', feeder: 'Фидер' },
    tower: { title: 'Опора № 14 · ВЛ 10 кВ', photo: 'Фото · 3', rows: [['Материал', 'Железобетон'], ['Назначение', 'Промежуточная'], ['Конструкция', 'Одностоечная'], ['Линии', 'ВЛ-10 Л-2 · ВЛ-0,4 Л-5'], ['Состояние', 'Удовлетворительное']], history: 'История', events: [['12.08', 'Осмотр · замер заземления'], ['03.06', 'Замена изолятора'], ['19.04', 'Паспортизация · фото']], add: 'Назначить работу' },
    monitor: { title: 'Ранжирование дефектов', cols: ['Объект', 'Дефект', 'Критичность', 'Ремонт'], rows: [['ТП-12', 'Перегрев контактов', 92, 'нед. 38'], ['ВЛ-10 Л-3', 'Наклон опоры', 78, 'нед. 40'], ['КЛ-0,4 Л-7', 'Снижение изоляции', 64, 'нед. 43'], ['ТП-5', 'Течь масла', 41, 'IV кв.']], schedule: 'График ремонтов', weeks: ['38', '39', '40', '41', '42', '43'], resources: 'Ресурсы', crew: '2 бригады · 46 ч·чел' },
    works: { title: 'Работы', columns: ['План', 'В работе', 'Выполнено'], items: [['Техприсоединение · 150 кВт', 'ТП-9', 0], ['Замена опоры № 31', 'ВЛ-10 Л-4', 0], ['Ремонт ТП-12', 'по состоянию', 1], ['Осмотр ВЛ-0,4 Л-5', 'ППР', 1], ['Замер нагрузок ТП-3', 'ППР', 2], ['Замена изоляторов', 'ВЛ-10 Л-2', 2]], crew: 'Бригада 3', crewStatus: 'на объекте · ТП-12', connection: 'Техприсоединение' },
    spec: { title: 'Техническое задание · проект 2417', fields: [['Тип подстанции', 'БКТП'], ['Трансформаторы', '2 × 1000 кВА'], ['Напряжение ВН / НН', '10 / 0,4 кВ'], ['РУ ВН', 'Одна секция шин'], ['Отходящие линии НН', '12'], ['Ток шин НН', '1600 А'], ['Исполнение', 'Утеплённое']], warning: 'Вводной выключатель 630 А несовместим с током шин 1600 А', fix: 'Заменить на 1600 А', recalc: 'Пересчитано: схема, спецификация, смета', build: 'Сформировать документы' },
    docs: { departments: ['Коммерческий отдел', 'Снабжение', 'Сборочный цех'], items: [['Однолинейная схема', 'Внешний вид', 'Договор поставки', 'Спецификация к договору', 'Счёт на оплату', 'Коммерческое предложение'], ['Спецификация на аппаратуру', 'Спецификация на материалы', 'Спецификация на логистику', 'Трудозатраты и накладные'], ['Внешний вид', 'Однолинейная схема', 'Спецификация на аппаратуру', 'Спецификация на материалы', 'Сборочные чертежи']], approved: 'согласовано', editing: 'на согласовании' },
    bom: { title: 'Спецификация и смета', cols: ['Шкаф', 'Модуль', 'Компонент', 'Цена, $'], rows: [['Шкаф 1 · ввод', 'QF-2 · автомат ACB', 'Выключатель 1600 А', '20 150'], ['Шкаф 2 · секция', 'QF-6.1 · автомат ACB', 'Выключатель 630 А', '11 010'], ['Шкаф 3 · отходящие', 'QF-6.2 · MCCB × 6', 'Автомат 250 А', '4 860'], ['Шкаф 4 · учёт', 'PM-1 · учёт', 'Счётчик, ТТ', '2 150'], ['Шкаф 5 · АВР', 'ATS-1', 'Контроллер АВР', '1 680']], total: 'Итого по проекту', totalValue: '39 850', maker: 'Производитель: настраивается' },
  },
}

const en: CasesCopy = {
  note: 'Client names and real data are withheld under NDA. Interfaces were redrawn for this site; the values shown in them are illustrative.',
  labels: { before: 'Before', after: 'After', results: 'What the client gained', modules: 'Modules' },
  flagship: {
    id: 'platform',
    sector: 'Electronic components distribution · international trade',
    title: 'A digital platform for a distributor: from customer request to customs clearance — and sales through AI agents',
    lead: 'One system for an international electronic components distributor: ERP and CRM for every department, a B2B portal for customers, a living catalog, AI search and ordering straight from the buyer’s AI assistant. We designed, built and keep evolving every module ourselves.',
    before: 'Procurement over the phone and in spreadsheets, documents by email, lead times estimated by hand, errors at every stage.',
    after: 'End-to-end automation from supplier sourcing to warehouse receipt: every order is visible at every step, and buyers find and order parts themselves — on the website or through their own AI agent.',
    metrics: [
      { value: '~8M', label: 'catalog items' },
      { value: '15+', label: 'global distributors in one search' },
      { value: '×17', label: 'faster catalog section pages' },
      { value: '~1 min', label: 'from AI-assistant request to invoice' },
    ],
    modules: [
      {
        id: 'erp',
        tab: 'ERP & CRM',
        title: 'The company’s operational core',
        lead: 'The whole deal chain in one system — from customer request to closing documents. 100+ people work in it every day: management, sales, procurement, logistics, engineers, warehouse and couriers.',
        points: [
          'Procurement: pricing, licensing, certification and lead times',
          'Payments: payment control under the supplier country’s tax rules, installments and prepayments, contracts and invoices in one place',
          'Logistics: dispatch, consolidation at transit warehouses, carrier tracking and warehouse receipt',
          'Customs: automated declarant workflows, document submission, licenses and certificates',
          'Integrations with accounting, WMS, CRM and carriers — no duplicate data entry',
        ],
      },
      {
        id: 'portal',
        tab: 'B2B portal',
        title: 'Self-service for customers',
        lead: 'Customers find a part, compare offers and place an order themselves — no back-and-forth with a manager.',
        points: [
          'Offers from 15+ global distributors in a single table',
          'Part-number search and real-time price comparison — with price breaks, minimum order quantity and pack multiples',
          'The best offer is highlighted automatically; ordering takes a couple of clicks',
          'Delivery tracking at every stage',
        ],
      },
      {
        id: 'catalog',
        tab: 'Catalog',
        title: 'A living catalog of ~8 million items',
        lead: 'The foundation of every service: normalized data that people, search engines and AI agents all understand.',
        points: [
          '~8M items in ~940 categories from ~4,700 manufacturers, ~80M parsed technical parameters',
          'Smart search: type “cer cap” and the right category opens with filters already set',
          'Product page: clear description, datasheet, market prices and stock, 3-month price trend, demand and parametric alternatives',
          'Updated daily from customer searches, the engineering system and supplier data',
          'Built for indexing: readable URLs, bilingual descriptions on almost every item, a filtered category page in ~1 s instead of ~17',
        ],
      },
      {
        id: 'search',
        tab: 'AI search',
        title: 'Any kind of spec — into a ready order',
        lead: 'Customers don’t need to prepare data: the system understands what it is looking at and finds the best offers.',
        points: [
          'Part numbers, parametric descriptions and free text mixed together — up to 100 lines per search',
          'Files in any format — spreadsheets, PDFs, documents, photos and scans; headers and noise are dropped automatically: 70 lines of a document become 13 items',
          '“Resistor 10 kΩ 0402 1%” turns into matching part numbers with supplier offers',
          'Alternatives by defining parameters, not by similar letters in the name',
          'Best price with MOQ, multiples and stock taken into account — straight to the cart',
        ],
      },
      {
        id: 'agent',
        tab: 'AI agent',
        title: 'A new sales channel: buying from the customer’s AI assistant',
        lead: 'We connected the store to AI agents via the MCP standard. Any AI agent that supports MCP works with the store directly: the customer sets the task and confirms — the agent does the rest.',
        points: [
          'Input: a list of part numbers, a parametric description or a BOM of up to 50 lines',
          'Search → selection → order → invoice in about a minute',
          'An optimizer assembles the best-value order with MOQ, multiples, stock and lead times in mind',
          'Orders are placed only on behalf of the customer’s company and only after confirmation; the invoice arrives right in the chat',
        ],
      },
    ],
  },
  energy: [
    {
      id: 'grid',
      sector: 'Energy · electrical networks',
      title: 'A digital model of the power grid to the CIM standard',
      lead: 'A software suite for a grid operator within a national infrastructure digitization program. It builds a detailed digital model of the whole enterprise — network, equipment, measurements and field work — and keeps it current: changes appear in the system as soon as the work is done.',
      standard: 'CIM · IEC 61970-301 · IEC 61968-11',
      before: 'Engineers walked the lines on foot, data lived in notebooks and scattered spreadsheets, diagrams on paper. Before every tariff review the figures were collected by hand.',
      after: 'One digital model of the network, equipment and work that cannot be distorted, deleted or hidden. Data for tariffs, repairs and grid connections is exported in a couple of clicks.',
      metrics: [
        { value: 'Multi-fold', label: 'reduction in the effort to plan field work' },
        { value: '1 model', label: 'instead of paper diagrams and scattered spreadsheets' },
        { value: 'CIM', label: 'the international standard for grid models and data exchange' },
        { value: '∞', label: 'seats — the license cost does not depend on them' },
      ],
      modules: [
        {
          id: 'grid-model',
          tab: 'Network model',
          title: 'A unified electrical connectivity diagram',
          lead: 'The topological network model is the foundation of the dispatch center and integrates with existing dispatch systems.',
          points: [
            'An interactive network map tied to terrain — on GIS and online maps',
            'Segmentation by voltage class, cable and overhead lines',
            'A power line editor with KML and GPX import and a substation editor with single-line diagrams',
            'An asset model and reference data linked to the diagram — the basis of the calculation model',
            'Data exchange over the standard CIM protocol lowers the cost of integrating other systems',
          ],
        },
        {
          id: 'grid-asset',
          tab: 'Asset passports',
          title: 'A full passport for every network asset',
          lead: 'Every tower, line, substation and piece of equipment has its specifications, photos, measurements and action history in the system.',
          points: [
            'Equipment is visible on the map: crews easily find an asset on site by its dispatch name',
            'Tower registration with photos: material, purpose, construction and the lines it carries',
            'Work history, measurements and passports are stored in the system and never lost',
            'Data comes in from every service involved and is instantly available to the people responsible',
          ],
        },
        {
          id: 'grid-monitor',
          tab: 'Monitoring',
          title: 'Equipment condition and repair priorities',
          lead: 'The measurement model is linked to the network and asset models: the system sees the technical condition and suggests what to repair first.',
          points: [
            'Equipment condition monitoring tied to the network model',
            'Condition analysis and assessment — the basis for reconstruction and re-equipment programs',
            'Algorithms rank defects by criticality and by the importance of the equipment',
            'An optimal repair schedule with the resources required',
            'Investment programs come together much faster: equipment is already ranked by priority',
          ],
        },
        {
          id: 'grid-works',
          tab: 'Repairs & connections',
          title: 'Managing work in the field',
          lead: 'The system tracks repairs and new grid connections, and the dispatcher always sees where each crew is.',
          points: [
            'Repair program planning: preventive, condition-based or combined maintenance',
            'Time and staffing standards for every type of work are built into the system',
            'Resource estimates based on the current reference books',
            'Collecting data for new grid connections is faster, so customers get connected sooner',
          ],
        },
      ],
      results: [
        { title: 'Current data', text: 'Network changes are reflected right after the work — enterprise information is always up to date' },
        { title: 'Protected information', text: 'With full implementation, network data cannot be distorted, deleted or hidden; access is role-based' },
        { title: 'Fixed pricing', text: 'The license is not tied to the number of seats' },
        { title: 'Any device', text: 'Browser-based — in the office, the control room and the field' },
        { title: 'Less key-person risk', text: 'Knowledge of the network lives in the system, not with a few people; the interface takes a short training to master' },
        { title: 'Flexibility', text: 'Adapts to different equipment and grows with new modules' },
      ],
    },
    {
      id: 'panel',
      sector: 'Energy · engineering configurator',
      title: 'A transformer substation with full documentation — in 10 minutes',
      lead: 'A web system for a major energy-sector enterprise: complete calculation of package transformer substations of all main types and of low-voltage switchgear, with technical and commercial documentation. An engineer or sales manager describes the requirements — the system finds the optimal solution and prepares documents for every department.',
      before: 'Layout, appearance, equipment selection, specifications and budget were all done by hand — hours or even days per project. Any change to the requirements meant recalculating from scratch.',
      after: 'Requirements in — an optimal design and a complete document package out, in 10 minutes or less. When the requirements change, the system recalculates only what is affected.',
      metrics: [
        { value: '≤10 min', label: 'full substation calculation with documentation' },
        { value: '10+', label: 'documents per project — generated automatically' },
        { value: '3 teams', label: 'get their own package: sales, procurement, production' },
        { value: '0', label: 'incompatible options — the system won’t allow them' },
      ],
      modules: [
        {
          id: 'panel-spec',
          tab: 'Requirements',
          title: 'Technical requirements online',
          lead: 'All key requirements are captured in one form and can be adjusted at any moment — the design updates instantly.',
          points: [
            'Selection logic accounts for technical requirements, manufacturer catalogs, guides and standards',
            'Hints simplify selection, and incompatible options simply cannot be chosen',
            'Manufacturer catalog data is already in the system',
            'When requirements change, adjust only the affected settings — the system recalculates the rest',
          ],
        },
        {
          id: 'panel-scheme',
          tab: 'Scheme & layout',
          title: 'Single-line diagram and appearance — automatically',
          lead: 'An interactive editor with a library of functional units and automatic calculation of installation parameters.',
          points: [
            'Single-line diagrams built from a library of functional units',
            'Automatic calculation of installation parameters',
            'A to-scale view of appearance and dimensions — the client sees the product before it is built',
            'One design methodology, whoever the engineer is',
          ],
        },
        {
          id: 'panel-docs',
          tab: 'Documents',
          title: 'Its own document package for every department',
          lead: 'Documents are generated automatically, edited online, and go through approval and registration in related departments.',
          points: [
            'Sales: single-line diagram, general view, supply contract and its specification, invoice, commercial proposal',
            'Procurement: specifications for equipment, materials, logistics, labor and overheads',
            'Assembly shop: general view, single-line diagram, specifications and assembly drawings',
            'Export to PDF, XLS and DXF',
          ],
        },
        {
          id: 'panel-bom',
          tab: 'Estimate & setup',
          title: 'A transparent estimate, tuned to the manufacturer',
          lead: 'Every cabinet, module and component comes with a price: management sees exactly what the project cost is made of.',
          points: [
            'Specification by cabinet, module and component with prices — automatically',
            'Transparent project costing for management',
            'Universal setup for a specific manufacturer and enterprise',
            'Integration with other software suites and information systems',
          ],
        },
      ],
      results: [
        { title: 'Proposals in minutes', text: 'Clients get complete technical and commercial information fast' },
        { title: 'Fewer errors', text: 'Incompatible equipment cannot be selected' },
        { title: 'Less routine', text: 'Staff are freed from manual layout, equipment selection and calculations' },
        { title: 'Transparency', text: 'Management sees the full project costing' },
        { title: 'See it before it’s built', text: 'Appearance and dimensions are visible to scale in advance' },
        { title: 'One standard', text: 'Every project follows the same methodology, whoever runs it' },
      ],
    },
  ],
  ui: {
    erp: { columns: ['Requests', 'Quoting', 'Quotes ready', 'Sent to customer'], modules: ['Procurement', 'Payments', 'Logistics', 'Customs'], search: 'Search by part number or brand', qty: 'pcs' },
    portal: { search: 'Part number', qty: 'Qty', find: 'Search', supplier: 'Supplier', stock: 'In stock', lead: 'Lead time', price: 'Price, $', total: 'Total, $', best: 'Best', weeks: 'wk', suppliers: ['Distributor · Asia', 'Warehouse · Europe', 'Verified seller', 'Authorized distributor'] },
    catalog: { query: 'cer cap', section: 'Ceramic capacitors', positions: '~430K items', filters: 'Filters', apply: 'Apply', relevance: 'Relevance', partNumber: 'Part number', maker: 'Manufacturer', median: 'median price', inStock: 'in stock at 6 suppliers', demand: 'Demand', analogs: 'Alternatives', datasheet: 'Datasheet', offers: 'Live offers', trend: '3 months' },
    search: { title: 'AI search', rows: ['MAX3485ESA 700', 'resistor 10 kΩ 0402 1% 25', 'BAV99 100', 'capacitor 100 nF 50 V X7R 0603 3000', 'some unclear line'], exact: 'Exact part number', byParams: 'By parameters · 5 options', analog: 'alternative', unknown: 'Refine line', counter: '2 part numbers · 3 descriptions', file: 'spec.pdf', fileResult: '70 lines → 13 items, 57 dropped', find: 'Search', toCart: 'Add to cart' },
    agent: {
      title: 'Customer’s AI assistant · MCP',
      user1: 'Find BAV99, I need 1,000 pcs',
      tool1: 'search_partnumber',
      agent1: 'Found ~470 offers from 16 suppliers. ~23M pcs in stock, fastest delivery — 4 days.',
      user2: 'Pick the cheapest option with delivery within 14 days',
      tool2: 'get_best_offer',
      agent2: '1,000 pcs at $0.004 — $4.00 total, 7 days, free pickup.',
      confirm: 'Place the order?',
      user3: 'Yes, place it and send the invoice',
      tool3: 'create_order · get_invoice',
      agent3: 'Order placed on behalf of your company. Invoice attached.',
      invoice: 'Invoice.pdf',
      typing: 'agent is working with the store…',
    },
    grid: { editor: 'Editor', scheme: 'Network scheme', layers: ['Power lines', 'Substations', 'Towers', 'Subscribers', 'Monitoring'], legend: ['110 kV', '10 kV', '0.4 kV'], substation: 'SS-12 · 10/0.4 kV', search: 'Address or asset' },
    panel: { units: 'Functional units', settings: 'Settings', params: [['Voltage', '380 V'], ['Frequency', '50 Hz'], ['Busbar current', '630 A'], ['Protection', 'IP42'], ['Spare', '10 %']], front: 'Front view', export: 'Export', feeder: 'Feeder' },
    tower: { title: 'Tower #14 · 10 kV line', photo: 'Photos · 3', rows: [['Material', 'Reinforced concrete'], ['Purpose', 'Intermediate'], ['Construction', 'Single-pole'], ['Lines', '10 kV L-2 · 0.4 kV L-5'], ['Condition', 'Satisfactory']], history: 'History', events: [['08/12', 'Inspection · earthing test'], ['06/03', 'Insulator replaced'], ['04/19', 'Registered · photos']], add: 'Assign work' },
    monitor: { title: 'Defect ranking', cols: ['Asset', 'Defect', 'Criticality', 'Repair'], rows: [['SS-12', 'Contact overheating', 92, 'wk 38'], ['10 kV L-3', 'Tower tilt', 78, 'wk 40'], ['0.4 kV C-7', 'Insulation loss', 64, 'wk 43'], ['SS-5', 'Oil leak', 41, 'Q4']], schedule: 'Repair schedule', weeks: ['38', '39', '40', '41', '42', '43'], resources: 'Resources', crew: '2 crews · 46 man-hours' },
    works: { title: 'Work orders', columns: ['Planned', 'In progress', 'Done'], items: [['Grid connection · 150 kW', 'SS-9', 0], ['Replace tower #31', '10 kV L-4', 0], ['Repair SS-12', 'condition-based', 1], ['Inspect 0.4 kV L-5', 'preventive', 1], ['Load test SS-3', 'preventive', 2], ['Replace insulators', '10 kV L-2', 2]], crew: 'Crew 3', crewStatus: 'on site · SS-12', connection: 'Grid connection' },
    spec: { title: 'Requirements · project 2417', fields: [['Substation type', 'Block-type'], ['Transformers', '2 × 1000 kVA'], ['HV / LV voltage', '10 / 0.4 kV'], ['HV switchgear', 'Single busbar'], ['LV outgoing feeders', '12'], ['LV busbar current', '1600 A'], ['Enclosure', 'Insulated']], warning: '630 A incoming breaker is incompatible with a 1600 A busbar', fix: 'Replace with 1600 A', recalc: 'Recalculated: diagram, specification, estimate', build: 'Generate documents' },
    docs: { departments: ['Sales', 'Procurement', 'Assembly shop'], items: [['Single-line diagram', 'General view', 'Supply contract', 'Contract specification', 'Invoice', 'Commercial proposal'], ['Equipment specification', 'Materials specification', 'Logistics specification', 'Labor & overheads'], ['General view', 'Single-line diagram', 'Equipment specification', 'Materials specification', 'Assembly drawings']], approved: 'approved', editing: 'in review' },
    bom: { title: 'Specification & estimate', cols: ['Cabinet', 'Module', 'Component', 'Price'], rows: [['Cabinet 1 · incomer', 'QF-2 · ACB', '1600 A breaker', '20 150'], ['Cabinet 2 · bus section', 'QF-6.1 · ACB', '630 A breaker', '11 010'], ['Cabinet 3 · feeders', 'QF-6.2 · MCCB × 6', '250 A breaker', '4 860'], ['Cabinet 4 · metering', 'PM-1 · metering', 'Meter, CTs', '2 150'], ['Cabinet 5 · ATS', 'ATS-1', 'ATS controller', '1 680']], total: 'Project total', totalValue: '39 850', maker: 'Manufacturer: configurable' },
  },
}

export const cases: Record<Lang, CasesCopy> = { ru, en, de, es, zh, th }
